import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ArrowRight, CheckCircle2, ChevronLeft, ChevronRight, CopyCheck, Filter, IndianRupee, PauseCircle, Replace, Search, Wallet, XCircle } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "./ui/hover-card";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { MultiSelect } from "./ui/multi-select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { fmtDate, fmtINR } from "../lib/api";
import { AreaFlagBadge, pickFlag } from "./AreaFlagsBanner";

/**
 * Phase 101 — preview of every row in an uploaded demand file, before Confirm.
 * Phase 102 — `variant="tp-update"` reuses the same table for the Third Party Bulk Update: the
 * Create/Update action column is replaced by a "Changes" column (field-by-field diff + demand to add).
 * Phase 103 — `variant="payment-import"`: legacy payment rows (Date, Receipt, Mode, Amount) with an
 * "Allocation" column showing how the FIFO engine will apply the money (demands + advance).
 *
 * Filter by Zone → Ward → Mohalla (values as written in the file / current location for updates),
 * status (and action / change type); tick / untick rows. Error rows are shown but locked — they can
 * never be processed. `selected` is a Set of row numbers owned by the parent (only those rows are sent).
 */
const ALL = "__all__";
const BLANK = "__blank__";
const PAGE_SIZE = 100;

const STATUS_META = {
  valid: { label: "Valid", icon: CheckCircle2, cls: "text-emerald-700" },
  warning: { label: "Warning", icon: AlertTriangle, cls: "text-amber-700" },
  error: { label: "Error", icon: XCircle, cls: "text-red-700" },
  // Phase 138 (payment-import): locked because the receipt + amount already exist — neutral, not an error
  already_recorded: { label: "Already recorded", icon: CopyCheck, cls: "text-muted-foreground" },
};

const keyOf = (v) => (v == null || v === "" ? BLANK : String(v));
const labelOf = (k) => (k === BLANK ? "— blank —" : k);
const uniq = (rows, field) => {
  const seen = new Map();
  rows.forEach((r) => { const k = keyOf(r[field]); if (!seen.has(k)) seen.set(k, labelOf(k)); });
  return [...seen.entries()].sort((a, b) => (a[0] === BLANK ? 1 : b[0] === BLANK ? -1 : a[1].localeCompare(b[1], undefined, { numeric: true })));
};
const fmtVal = (v) => (v == null || v === "" ? "—" : String(v));

// change-type filter for the tp-update variant
const CHANGE_FILTER = {
  [ALL]: () => true,
  fields: (r) => (r.changes || []).length > 0,
  demand: (r) => !!r.demand,
  noop: (r) => r.status !== "error" && !!r.no_op,
};

// allocation filter for the payment-import variant
const ALLOC_FILTER = {
  [ALL]: () => true,
  allocated: (r) => (r.allocations || []).length > 0 && !(r.advance_amount > 0),
  split: (r) => (r.allocations || []).length > 1,
  advance: (r) => r.advance_amount > 0,
  suffixed: (r) => !!r.legacy_receipt_number,
  // Phase 138
  replace: (r) => r.action === "replace" && r.status !== "error",
  already: (r) => r.action === "already_recorded",
  // Phase 194: the PIN already has manual (app-entered) receipts — decided in the import review
  review: (r) => r.action === "review" && r.status !== "error",
};

/** FIFO allocation summary for one legacy payment row (Phase 103). */
function AllocationCell({ row }) {
  const allocs = row.allocations || [];
  if (row.status === "error") return <span className="text-xs text-muted-foreground" data-testid={`upload-preview-alloc-${row.row_number}`}>—</span>;
  const adv = row.advance_amount || 0;
  return (
    <HoverCard openDelay={150} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()}
          className="text-left rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid={`upload-preview-alloc-${row.row_number}`}>
          <span className="inline-flex items-center gap-1.5">
            {allocs.length > 0 && (
              <Badge variant="outline" className="text-[10px] font-normal bg-sky-50 text-sky-800 border-sky-200">
                {allocs.length} demand{allocs.length === 1 ? "" : "s"}
              </Badge>
            )}
            {adv > 0 && (
              <Badge variant="outline" className="text-[10px] font-normal bg-amber-50 text-amber-800 border-amber-200 gap-0.5">
                <Wallet className="h-2.5 w-2.5" /> advance {fmtINR(adv)}
              </Badge>
            )}
          </span>
          {allocs[0] && (
            <p className="text-[11px] text-muted-foreground truncate max-w-[220px]">
              {allocs[0].fy_label || "FY"} · {fmtINR(allocs[0].amount)}{allocs.length > 1 ? ` (+${allocs.length - 1} more)` : ""}
            </p>
          )}
          {allocs.length === 0 && adv > 0 && <p className="text-[11px] text-muted-foreground">No open demand — credited as advance</p>}
        </button>
      </HoverCardTrigger>
      <HoverCardContent align="start" className="w-80 p-3" onClick={(e) => e.stopPropagation()}>
        <p className="text-xs font-semibold mb-2">Row {row.row_number} · {row.pin} · {fmtINR(row.amount)}</p>
        <ul className="space-y-1.5 max-h-60 overflow-y-auto">
          {allocs.map((a, i) => (
            <li key={i} className="text-xs flex items-center justify-between gap-2">
              <span><span className="font-medium">{a.fy_label || "FY"}</span> <span className="text-muted-foreground">· {a.tax_head}</span></span>
              <span className="tabular-nums font-medium">{fmtINR(a.amount)}<span className="text-muted-foreground font-normal"> → bal {fmtINR(a.balance_after)}</span></span>
            </li>
          ))}
          {adv > 0 && (
            <li className="text-xs flex items-center justify-between gap-2 pt-1.5 border-t">
              <span className="font-medium text-amber-800">Advance credit</span>
              <span className="tabular-nums font-medium">{fmtINR(adv)}</span>
            </li>
          )}
          {row.outstanding_before != null && (
            <li className="text-[11px] text-muted-foreground pt-1.5 border-t">
              Outstanding before {fmtINR(row.outstanding_before)} → after {fmtINR(row.outstanding_after)}
            </li>
          )}
        </ul>
      </HoverCardContent>
    </HoverCard>
  );
}

/** Field-by-field diff for one Third Party update row (Phase 102). */
function ChangesCell({ row }) {
  const changes = row.changes || [];
  const total = changes.length + (row.demand ? 1 : 0);
  if (row.status === "error") return <span className="text-xs text-muted-foreground" data-testid={`upload-preview-changes-${row.row_number}`}>—</span>;
  if (total === 0) return <span className="text-xs text-muted-foreground italic" data-testid={`upload-preview-changes-${row.row_number}`}>No change</span>;
  const first = changes[0];
  return (
    <HoverCard openDelay={150} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()}
          className="text-left rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid={`upload-preview-changes-${row.row_number}`}>
          <span className="inline-flex items-center gap-1.5">
            <Badge variant="outline" className="text-[10px] font-normal bg-sky-50 text-sky-800 border-sky-200">
              {changes.length} field{changes.length === 1 ? "" : "s"}
            </Badge>
            {row.demand && (
              <Badge variant="outline" className="text-[10px] font-normal bg-emerald-50 text-emerald-800 border-emerald-200 gap-0.5">
                <IndianRupee className="h-2.5 w-2.5" /> demand
              </Badge>
            )}
          </span>
          {first && (
            <p className="text-[11px] text-muted-foreground truncate max-w-[220px]">
              {first.label}: {fmtVal(first.from)} → {fmtVal(first.to)}{changes.length > 1 ? ` (+${changes.length - 1} more)` : ""}
            </p>
          )}
        </button>
      </HoverCardTrigger>
      <HoverCardContent align="start" className="w-80 p-3" onClick={(e) => e.stopPropagation()}>
        <p className="text-xs font-semibold mb-2">Row {row.row_number} · {row.pin}</p>
        <ul className="space-y-1.5 max-h-60 overflow-y-auto">
          {changes.map((c, i) => (
            <li key={i} className="text-xs">
              <span className="font-medium">{c.label}</span>
              {c.create && <Badge variant="outline" className="ml-1 text-[9px] font-normal bg-amber-50 text-amber-800 border-amber-200">new master</Badge>}
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <span className="line-through decoration-muted-foreground/60 truncate max-w-[120px]" title={fmtVal(c.from)}>{fmtVal(c.from)}</span>
                <ArrowRight className="h-3 w-3 shrink-0" />
                <span className="text-foreground font-medium truncate max-w-[130px]" title={fmtVal(c.to)}>{fmtVal(c.to)}</span>
              </div>
            </li>
          ))}
          {row.demand && (
            <li className="text-xs pt-1.5 border-t">
              <span className="font-medium text-emerald-800">Demand to add</span>
              <div className="text-muted-foreground">{row.demand.fy_label} · <span className="text-foreground font-medium tabular-nums">{fmtINR(row.demand.amount)}</span></div>
            </li>
          )}
        </ul>
      </HoverCardContent>
    </HoverCard>
  );
}

export function UploadPreviewTable({ rows, selected, onChange, variant = "demand", flagFilter = null, onFlagFilterChange }) {
  const isUpdate = variant === "tp-update";
  const isPayment = variant === "payment-import";
  // Phase 176: zone / ward / mohalla are multi-select (arrays of option keys; [] = all)
  const [zone, setZone] = useState([]);
  const [ward, setWard] = useState([]);
  const [mohalla, setMohalla] = useState([]);
  const [status, setStatus] = useState(ALL);
  const [action, setAction] = useState(ALL);      // demand variant: create | update — tp-update: change type
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  // Phase 161: area-flag filter — "any" or a flag code; the banner's "Show rows" drives it through `flagFilter`
  const [flagLocal, setFlagLocal] = useState(ALL);
  const flag = flagFilter ?? flagLocal;
  const setFlag = (v) => { setFlagLocal(v); onFlagFilterChange?.(v === ALL ? null : v); };
  useEffect(() => { if (flagFilter != null) setFlagLocal(flagFilter); }, [flagFilter]);
  const hasFlags = useMemo(() => rows.some((r) => (r.area_flags || []).length > 0), [rows]);
  const flagOk = (r) => flag === ALL || (flag === "any" ? (r.area_flags || []).length > 0 : (r.area_flags || []).some((f) => f.code === flag));
  const flagCodes = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => (r.area_flags || []).forEach((f) => { if (!m.has(f.code)) m.set(f.code, f.label); }));
    return [...m.entries()];
  }, [rows]);

  // cascading option lists built from the file's own combinations (any selected zone / ward matches)
  const zoneRows = useMemo(() => (zone.length === 0 ? rows : rows.filter((r) => zone.includes(keyOf(r.zone)))), [rows, zone]);
  const wardRows = useMemo(() => (ward.length === 0 ? zoneRows : zoneRows.filter((r) => ward.includes(keyOf(r.ward)))), [zoneRows, ward]);
  const zoneOptions = useMemo(() => uniq(rows, "zone"), [rows]);
  const wardOptions = useMemo(() => uniq(zoneRows, "ward"), [zoneRows]);
  const mohallaOptions = useMemo(() => uniq(wardRows, "mohalla"), [wardRows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const actionOk = isUpdate ? (CHANGE_FILTER[action] || CHANGE_FILTER[ALL])
      : isPayment ? (ALLOC_FILTER[action] || ALLOC_FILTER[ALL])
        : (r) => action === ALL || r.action === action;
    return wardRows.filter((r) =>
      (mohalla.length === 0 || mohalla.includes(keyOf(r.mohalla)))
      && (status === ALL || r.status === status)
      && actionOk(r)
      && flagOk(r)
      && (!q || String(r.pin || "").toLowerCase().includes(q) || String(r.owner_name || "").toLowerCase().includes(q)
        || String(r.house_no || "").toLowerCase().includes(q) || String(r.row_number).includes(q)
        || String(r.receipt || "").toLowerCase().includes(q) || String(r.cheque_no || "").toLowerCase().includes(q)));
  }, [wardRows, mohalla, status, action, search, isUpdate, isPayment, flag]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setPage(1); }, [zone, ward, mohalla, status, action, search, flag]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // picking parents prunes children that no longer belong to the (new) parent selection
  const pickZone = (v) => {
    setZone(v);
    const zr = v.length === 0 ? rows : rows.filter((r) => v.includes(keyOf(r.zone)));
    const wardOk = new Set(uniq(zr, "ward").map(([k]) => k));
    const nextWard = ward.filter((k) => wardOk.has(k));
    setWard(nextWard);
    const wr = nextWard.length === 0 ? zr : zr.filter((r) => nextWard.includes(keyOf(r.ward)));
    const mohOk = new Set(uniq(wr, "mohalla").map(([k]) => k));
    setMohalla(mohalla.filter((k) => mohOk.has(k)));
  };
  const pickWard = (v) => {
    setWard(v);
    const wr = v.length === 0 ? zoneRows : zoneRows.filter((r) => v.includes(keyOf(r.ward)));
    const mohOk = new Set(uniq(wr, "mohalla").map(([k]) => k));
    setMohalla(mohalla.filter((k) => mohOk.has(k)));
  };
  const resetFilters = () => { setZone([]); setWard([]); setMohalla([]); setStatus(ALL); setAction(ALL); setSearch(""); setFlag(ALL); };
  const filtersActive = zone.length > 0 || ward.length > 0 || mohalla.length > 0 || status !== ALL || action !== ALL || search.trim() !== "" || flag !== ALL;

  const selectable = (r) => r.status !== "error";
  const shownSelectable = filtered.filter(selectable);
  const shownSelected = shownSelectable.filter((r) => selected.has(r.row_number)).length;
  const allShown = shownSelectable.length > 0 && shownSelected === shownSelectable.length;
  const someShown = shownSelected > 0 && !allShown;

  const toggle = (r) => {
    if (!selectable(r)) return;
    const next = new Set(selected);
    if (next.has(r.row_number)) next.delete(r.row_number); else next.add(r.row_number);
    onChange(next);
  };
  const setShown = (on) => {
    const next = new Set(selected);
    shownSelectable.forEach((r) => (on ? next.add(r.row_number) : next.delete(r.row_number)));
    onChange(next);
  };
  const colCount = isPayment ? 11 : 10;

  return (
    <div className="space-y-3" data-testid="upload-preview" data-variant={variant}>
      {/* filters */}
      <div className="flex flex-wrap items-center gap-2" data-testid="upload-preview-filters">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <MultiSelect options={zoneOptions.map(([k, l]) => ({ value: k, label: l }))} value={zone} onChange={pickZone}
          allLabel="All zones" noun="zone" placeholder="Search zones…" className="h-9 w-40" testId="upload-preview-zone" />
        <MultiSelect options={wardOptions.map(([k, l]) => ({ value: k, label: l }))} value={ward} onChange={pickWard}
          allLabel="All wards" noun="ward" placeholder="Search wards…" className="h-9 w-40" testId="upload-preview-ward" />
        <MultiSelect options={mohallaOptions.map(([k, l]) => ({ value: k, label: l }))} value={mohalla} onChange={setMohalla}
          allLabel="All mohallas" noun="mohalla" placeholder="Search mohallas…" className="h-9 w-44" testId="upload-preview-mohalla" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-36" data-testid="upload-preview-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            <SelectItem value="valid">Valid</SelectItem>
            <SelectItem value="warning">Warnings</SelectItem>
            <SelectItem value="error">Errors</SelectItem>
          </SelectContent>
        </Select>
        {isPayment ? (
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="h-9 w-48" data-testid="upload-preview-action"><SelectValue placeholder="Allocation" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All allocations</SelectItem>
              <SelectItem value="allocated">Fully against demands</SelectItem>
              <SelectItem value="split">Split across demands</SelectItem>
              <SelectItem value="advance">Creates advance credit</SelectItem>
              <SelectItem value="suffixed">Receipt no. adjusted</SelectItem>
              <SelectItem value="replace">Replaces existing entry</SelectItem>
              <SelectItem value="already">Already recorded (skipped)</SelectItem>
              <SelectItem value="review">Needs review (manual receipts)</SelectItem>
            </SelectContent>
          </Select>
        ) : isUpdate ? (
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="h-9 w-44" data-testid="upload-preview-action"><SelectValue placeholder="Changes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All changes</SelectItem>
              <SelectItem value="fields">Field changes</SelectItem>
              <SelectItem value="demand">Adds a demand</SelectItem>
              <SelectItem value="noop">Nothing to update</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="h-9 w-40" data-testid="upload-preview-action"><SelectValue placeholder="Action" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Create &amp; update</SelectItem>
              <SelectItem value="update">Update existing</SelectItem>
              <SelectItem value="create">Create new</SelectItem>
            </SelectContent>
          </Select>
        )}
        {hasFlags && !isPayment && (
          <Select value={flag} onValueChange={setFlag}>
            <SelectTrigger className="h-9 w-48" data-testid="upload-preview-flag"><SelectValue placeholder="Area flags" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Area flags: all rows</SelectItem>
              <SelectItem value="any">Any area flag</SelectItem>
              {flagCodes.map(([code, label]) => <SelectItem key={code} value={code}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <div className="relative flex-1 min-w-[180px]">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={isPayment ? "PIN, owner, receipt, cheque no or row #" : "PIN, owner, house no or row #"}
            className="h-9 pl-8" data-testid="upload-preview-search" />
        </div>
        {filtersActive && (
          <Button variant="ghost" size="sm" className="h-9 text-muted-foreground" onClick={resetFilters} data-testid="upload-preview-reset">Reset filters</Button>
        )}
      </div>

      {/* selection toolbar */}
      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="upload-preview-toolbar">
        <span className="text-muted-foreground" data-testid="upload-preview-shown">
          Showing <b className="text-foreground">{filtered.length.toLocaleString("en-IN")}</b> of {rows.length.toLocaleString("en-IN")} rows
          {filtersActive ? " (filtered)" : ""} · <b className="text-foreground">{shownSelected}</b> shown rows selected
        </span>
        <div className="flex items-center gap-1 ml-auto">
          <Button variant="outline" size="sm" className="h-8" onClick={() => setShown(true)} disabled={shownSelectable.length === 0 || allShown} data-testid="upload-preview-select-shown">
            Select shown ({shownSelectable.length})
          </Button>
          <Button variant="outline" size="sm" className="h-8" onClick={() => setShown(false)} disabled={shownSelected === 0} data-testid="upload-preview-clear-shown">
            Clear shown
          </Button>
        </div>
      </div>

      {/* table */}
      <div className="border rounded-lg overflow-auto max-h-[28rem]">
        <Table data-testid="upload-preview-table">
          <TableHeader className="sticky top-0 bg-card z-10">
            <TableRow>
              <TableHead className="w-10">
                <Checkbox checked={allShown ? true : someShown ? "indeterminate" : false} onCheckedChange={(v) => setShown(v === true)}
                  disabled={shownSelectable.length === 0} aria-label="Select all shown rows" data-testid="upload-preview-select-all" />
              </TableHead>
              <TableHead className="w-14">Row</TableHead>
              <TableHead>PIN</TableHead>
              <TableHead>Owner</TableHead>
              {isPayment ? (<>
                <TableHead>Date</TableHead>
                <TableHead>Receipt</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Ward</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Allocation</TableHead>
                <TableHead>Credited to</TableHead>
              </>) : (<>
                <TableHead>Zone</TableHead>
                <TableHead>Ward</TableHead>
                <TableHead>Mohalla</TableHead>
                <TableHead className="text-right">{isUpdate ? "New demand" : "Demand"}</TableHead>
                <TableHead>{isUpdate ? "Changes" : "Action"}</TableHead>
              </>)}
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRows.length === 0 ? (
              <TableRow><TableCell colSpan={colCount} className="text-center text-sm text-muted-foreground py-8" data-testid="upload-preview-empty">
                No rows match these filters.
              </TableCell></TableRow>
            ) : pageRows.map((r) => {
              const already = isPayment && r.action === "already_recorded";
              const replaces = isPayment && r.action === "replace" && r.status !== "error";
              const reviews = isPayment && r.action === "review" && r.status !== "error";   // Phase 194
              const meta = (already ? STATUS_META.already_recorded : STATUS_META[r.status]) || STATUS_META.valid;
              const Icon = meta.icon;
              const locked = !selectable(r);
              const on = selected.has(r.row_number);
              const issues = [...(r.errors || []), ...(r.warnings || [])];
              return (
                <TableRow key={r.row_number} onClick={() => toggle(r)}
                  className={`${locked ? "opacity-60" : "cursor-pointer"} ${on ? "bg-primary/[0.04]" : ""} ${replaces && on ? "bg-amber-50/70" : ""} ${reviews && on ? "bg-indigo-50/60" : ""}`}
                  data-testid={`upload-preview-row-${r.row_number}`} data-selected={on ? "true" : "false"} data-status={r.status} data-action={r.action || undefined}
                  data-area-flags={(r.area_flags || []).map((f) => f.code).join(" ") || undefined}>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={on} disabled={locked} onCheckedChange={() => toggle(r)}
                      aria-label={`Select row ${r.row_number}`} data-testid={`upload-preview-check-${r.row_number}`} />
                  </TableCell>
                  <TableCell className="font-mono text-xs">{r.row_number}</TableCell>
                  <TableCell className="font-mono text-xs">{r.pin || <span className="text-muted-foreground">{isUpdate || isPayment ? "-" : "new"}</span>}</TableCell>
                  <TableCell className="text-sm">
                    {r.owner_name || <span className="text-muted-foreground">-</span>}
                    {r.house_no && <p className="text-[11px] text-muted-foreground">House {r.house_no}</p>}
                  </TableCell>
                  {isPayment ? (<>
                    <TableCell className="text-xs whitespace-nowrap" data-testid={`upload-preview-date-${r.row_number}`}>
                      {r.payment_date ? fmtDate(r.payment_date) : <span className="text-destructive">{r.date_raw || "-"}</span>}
                    </TableCell>
                    <TableCell className="font-mono text-xs" data-testid={`upload-preview-receipt-${r.row_number}`}>
                      {r.receipt || <span className="text-muted-foreground">-</span>}
                      {r.legacy_receipt_number && <p className="text-[10px] text-amber-700 font-sans">saved as {r.receipt_number}</p>}
                      {r.cheque_no && <p className="text-[10px] text-muted-foreground font-sans">Chq {r.cheque_no}</p>}
                    </TableCell>
                    <TableCell className="text-xs" data-testid={`upload-preview-mode-${r.row_number}`}>
                      {r.payment_mode || <span className="text-destructive">{r.mode_text || "-"}</span>}
                    </TableCell>
                    <TableCell className="text-xs">{r.ward || <span className="text-muted-foreground">-</span>}{r.zone && <p className="text-[10px] text-muted-foreground">{r.zone}</p>}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs font-medium" data-testid={`upload-preview-amount-${r.row_number}`}>
                      {r.amount != null ? fmtINR(r.amount) : <span className="text-destructive font-normal">-</span>}
                    </TableCell>
                    <TableCell><AllocationCell row={r} /></TableCell>
                    <TableCell className="text-xs" data-testid={`upload-preview-credit-${r.row_number}`}>
                      {r.credited_to_name ? (
                        <>
                          <p className="font-medium">{r.credited_to_name}{r.credited_to_employee_id ? <span className="text-muted-foreground font-normal"> ({r.credited_to_employee_id})</span> : null}</p>
                          <p className="text-[10px] text-muted-foreground">{r.credited_to_role === "supervisor" ? "Supervisor" : "Tax Collector"}</p>
                        </>
                      ) : r.property_id ? (
                        <Badge variant="outline" className="text-[10px] font-normal bg-violet-50 text-violet-800 border-violet-200">Imported (Legacy)</Badge>
                      ) : <span className="text-muted-foreground">-</span>}
                    </TableCell>
                  </>) : (<>
                    <TableCell className="text-xs">{r.zone || <span className="text-muted-foreground">-</span>}</TableCell>
                    <TableCell className="text-xs" data-testid={`upload-preview-ward-${r.row_number}`}>
                      {r.ward || <span className="text-muted-foreground">-</span>}
                      <AreaFlagBadge flag={pickFlag(r.area_flags, "ward")} rowNumber={r.row_number} cell="ward" />
                    </TableCell>
                    <TableCell className="text-xs" data-testid={`upload-preview-mohalla-${r.row_number}`}>
                      {r.mohalla || <span className="text-muted-foreground">-</span>}
                      <AreaFlagBadge flag={pickFlag(r.area_flags, "mohalla")} rowNumber={r.row_number} cell="mohalla" />
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs">
                      {isUpdate ? (r.demand ? fmtINR(r.demand.amount) : <span className="text-muted-foreground">—</span>) : fmtINR(r.demand_total)}
                    </TableCell>
                    <TableCell>
                      {isUpdate ? <ChangesCell row={r} /> : (
                        <Badge variant="outline" className={`text-[10px] font-normal ${r.action === "update" ? "bg-sky-50 text-sky-800 border-sky-200" : "bg-emerald-50 text-emerald-800 border-emerald-200"}`}
                          data-testid={`upload-preview-action-${r.row_number}`}>
                          {r.action === "update" ? "Update existing" : "Create new"}
                        </Badge>
                      )}
                    </TableCell>
                  </>)}
                  <TableCell className="max-w-[260px]">
                    {replaces && (
                      <Badge variant="outline" className="mb-1 text-[10px] font-normal bg-amber-100 text-amber-900 border-amber-300 gap-1" data-testid={`upload-preview-replace-${r.row_number}`}>
                        <Replace className="h-2.5 w-2.5" aria-hidden="true" /> Replaces existing entry
                      </Badge>
                    )}
                    {reviews && (
                      <Badge variant="outline" className="mb-1 text-[10px] font-normal bg-indigo-50 text-indigo-800 border-indigo-200 gap-1" data-testid={`upload-preview-review-${r.row_number}`}>
                        <PauseCircle className="h-2.5 w-2.5" aria-hidden="true" /> Needs review
                      </Badge>
                    )}
                    <span className={`inline-flex items-center gap-1 text-xs ${meta.cls}`} data-testid={`upload-preview-status-${r.row_number}`}>
                      <Icon className="h-3.5 w-3.5" /> {meta.label}{locked ? (already ? " · skipped" : " · locked") : ""}
                    </span>
                    {issues.length > 0 && (
                      <p className="text-[11px] text-muted-foreground truncate" title={issues.join("\n")}>{issues[0]}{issues.length > 1 ? ` (+${issues.length - 1} more)` : ""}</p>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-center gap-3" data-testid="upload-preview-pagination">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="upload-preview-prev"><ChevronLeft className="h-4 w-4 mr-1" /> Previous</Button>
          <Badge variant="secondary" className="text-xs">Page {page} of {pages}</Badge>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="upload-preview-next">Next <ChevronRight className="h-4 w-4 ml-1" /></Button>
        </div>
      )}
    </div>
  );
}
